import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiChevronDown, FiSearch, FiTrash2 } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { DashboardContent } from '@/layouts/dashboard';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Table, TableBody, TableCell, TableHeaderWrapper, TableRow, TableUser, TableUserContent, TableUserTitle } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ProfileResume, ProfileResumeBody, ProfileResumeName } from '@/components/custom/profile';
import { liveChallengesApi } from '@/services/liveChallenges';
import { useSession } from '@/hooks/useSession';
import usePermission from '@/hooks/usePermission';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { TiaBanner } from '@/components/custom/tia-banner';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { DESAFIO_AO_VIVO } from '@/pages/dashboard/home-teacher/teacher-character';

const STATUS_CONFIG = {
  draft: { label: 'Rascunho', variant: 'neutral-lightest' },
  published: { label: 'Publicado', variant: 'success-light' }
};

// Mapeamento "soft" (fundo claro + texto colorido) só para a tabela do tema
// playful — a lista default usa STATUS_CONFIG acima (fundo cheio).
const PLAYFUL_STATUS_BADGE = {
  draft: { label: 'Rascunho', variant: 'muted' },
  published: { label: 'Publicado', variant: 'success-soft' }
};

const CHALLENGE_TABLE_HEAD = [{ label: 'Criado por', width: 240 }, { label: 'Desafio' }, { label: 'Status', width: 120 }, { label: 'Disponibilidade', width: 140 }, { label: '', width: 280 }];

const STATUS_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'draft', label: 'Rascunho' },
  { value: 'published', label: 'Publicado' }
];

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <Badge
      variant={config.variant}
      size="xs"
      className="text-sm"
    >
      {config.label}
    </Badge>
  );
}

function ChallengeRow({ challenge, onRefresh, currentUserId, isAdmin }) {
  const navigate = useNavigate();
  const { id, title, taxonomy, status, created_at, total_questions, user, is_public } = challenge;

  const formattedDate = created_at ? new Date(created_at).toLocaleDateString('pt-BR') : '—';
  const profileName = user?.profile_name;
  const isAuthor = currentUserId != null && user?.id != null && String(user.id) === String(currentUserId);
  const canManage = isAuthor || isAdmin;

  const handleActivate = async () => {
    try {
      const response = await liveChallengesApi.activate(id);
      const activationId = response?.data?.id;
      navigate(`/admin/desafio-ao-vivo/painel/${activationId}`);
    } catch (err) {
      const existingId = err?.response?.data?.activation_id;
      if (err?.response?.status === 409 && existingId) {
        toast('Você já tem uma ativação em andamento deste desafio — retomando.', { icon: 'ℹ️' });
        navigate(`/admin/desafio-ao-vivo/painel/${existingId}`);
        return;
      }
      const message = err?.response?.data?.error || 'Erro ao ativar desafio.';
      toast.error(message);
    }
  };

  // Gatilho draft→published (contrato do plano backend: PATCH update com status permitido).
  const handlePublish = async () => {
    try {
      await liveChallengesApi.update(id, { status: 'published' });
      toast.success('Desafio publicado — pronto para ativar.');
      onRefresh?.();
    } catch (err) {
      // update do backend responde {errors: [strings]} (full_messages), não JSON:API
      toast.error(err?.response?.data?.errors?.[0] || 'Erro ao publicar desafio.');
    }
  };

  const handleDuplicate = async () => {
    try {
      await liveChallengesApi.duplicate(id);
      toast.success('Desafio duplicado em rascunho.');
      onRefresh();
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao duplicar desafio.';
      toast.error(message);
    }
  };

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await liveChallengesApi.destroy(id);
      toast.success('Desafio excluído.');
      setConfirmingDelete(false);
      onRefresh();
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao excluir desafio.';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <li className="hover:bg-primary-lightest/30 flex flex-col gap-3 border-b border-neutral-lightest bg-white px-5 py-3 transition-colors md:flex-row md:items-center md:gap-6">
      <div className="w-full md:w-56 md:shrink-0">
        {user ? (
          <ProfileResume>
            <AvatarWrapper
              size="sm"
              src={user.image}
              text={user.name}
              className="shrink-0"
            />
            <ProfileResumeBody>
              <ProfileResumeName title={user.name}>{user.name}</ProfileResumeName>
              {profileName && (
                <Badge
                  variant="neutral-lightest"
                  size="xs"
                  className="text-sm font-light"
                  title={profileName}
                >
                  {profileName}
                </Badge>
              )}
            </ProfileResumeBody>
          </ProfileResume>
        ) : (
          <span className="text-xs text-neutral-light">—</span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3">
          <h3 className="truncate text-sm font-semibold text-neutral-dark">{title}</h3>
          <StatusBadge status={status} />
          <Badge
            variant="neutral-lightest"
            size="xs"
            className="text-sm font-light"
          >
            {is_public ? 'Público' : 'Privado'}
          </Badge>
        </div>
        <p className="mt-0.5 text-sm text-neutral">
          {taxonomy?.name || '—'} &middot; {total_questions ?? 0} questões &middot; {formattedDate} &middot; Última ativação: {challenge.last_activation_at ? new Date(challenge.last_activation_at).toLocaleString('pt-BR') : '—'}
        </p>
      </div>

      <div className="flex flex-wrap justify-end gap-2 md:w-[450px]">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleDuplicate}
        >
          Duplicar para editar
        </Button>

        {canManage && status === 'draft' && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(`/admin/desafio-ao-vivo/editar/nome/${id}`)}
          >
            Editar
          </Button>
        )}

        {canManage && status === 'draft' && (
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handlePublish}
          >
            Publicar
          </Button>
        )}

        {status === 'published' && (canManage || is_public) && (
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handleActivate}
          >
            Ativar
          </Button>
        )}

        {canManage && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(`/admin/desafio-ao-vivo/${id}/ativacoes`)}
          >
            Ativações{typeof challenge.activations_count === 'number' ? ` (${challenge.activations_count})` : ''}
          </Button>
        )}

        {isAuthor && status === 'draft' && (
          <Button
            type="button"
            variant="outline-danger"
            size="sm"
            onClick={() => setConfirmingDelete(true)}
            title="Excluir desafio"
          >
            <FiTrash2 className="size-3.5" />
          </Button>
        )}
      </div>

      {confirmingDelete && (
        <ConfirmDeleteDialog
          challengeTitle={title}
          onConfirm={handleDelete}
          onCancel={() => setConfirmingDelete(false)}
          loading={deleting}
        />
      )}
    </li>
  );
}

// Linha da tabela do tema playful. Duplica os handlers de ChallengeRow em vez
// de extrair um hook compartilhado — mesmo critério de essay-monitoring.jsx
// (duplicado de propósito): garante que o tema default fique intocado,
// mesmo que a coluna de Ações mude de menu aqui no futuro.
function ChallengeTableRow({ challenge, onRefresh, currentUserId, isAdmin }) {
  const navigate = useNavigate();
  const { id, title, taxonomy, status, created_at, total_questions, user, is_public } = challenge;

  const formattedDate = created_at ? new Date(created_at).toLocaleDateString('pt-BR') : '—';
  const profileName = user?.profile_name;
  const isAuthor = currentUserId != null && user?.id != null && String(user.id) === String(currentUserId);
  const canManage = isAuthor || isAdmin;

  const handleActivate = async () => {
    try {
      const response = await liveChallengesApi.activate(id);
      const activationId = response?.data?.id;
      navigate(`/admin/desafio-ao-vivo/painel/${activationId}`);
    } catch (err) {
      const existingId = err?.response?.data?.activation_id;
      if (err?.response?.status === 409 && existingId) {
        toast('Você já tem uma ativação em andamento deste desafio — retomando.', { icon: 'ℹ️' });
        navigate(`/admin/desafio-ao-vivo/painel/${existingId}`);
        return;
      }
      const message = err?.response?.data?.error || 'Erro ao ativar desafio.';
      toast.error(message);
    }
  };

  const handlePublish = async () => {
    try {
      await liveChallengesApi.update(id, { status: 'published' });
      toast.success('Desafio publicado — pronto para ativar.');
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.errors?.[0] || 'Erro ao publicar desafio.');
    }
  };

  const handleDuplicate = async () => {
    try {
      await liveChallengesApi.duplicate(id);
      toast.success('Desafio duplicado em rascunho.');
      onRefresh();
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao duplicar desafio.';
      toast.error(message);
    }
  };

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await liveChallengesApi.destroy(id);
      toast.success('Desafio excluído.');
      setConfirmingDelete(false);
      onRefresh();
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao excluir desafio.';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const statusBadge = PLAYFUL_STATUS_BADGE[status] || PLAYFUL_STATUS_BADGE.draft;

  return (
    <TableRow>
      <TableCell>
        {user ? (
          <TableUser>
            <AvatarWrapper
              size="sm"
              src={user.image}
              text={user.name}
              className="shrink-0"
            />
            <TableUserContent>
              <TableUserTitle
                className="w-full truncate text-sm"
                title={user.name}
              >
                {user.name}
              </TableUserTitle>
              {profileName && <span className="w-full truncate text-sm font-light text-neutral-400">{profileName}</span>}
            </TableUserContent>
          </TableUser>
        ) : (
          <span className="text-sm text-neutral-light">—</span>
        )}
      </TableCell>

      <TableCell>
        <p
          className="mb-0 truncate text-sm font-bold"
          style={{ color: '#222731' }}
          title={title}
        >
          {title}
        </p>
        <p className="mb-0 mt-0.5 truncate text-sm text-neutral-400">
          {taxonomy?.name || '—'} · {total_questions ?? 0} questões · {formattedDate}
        </p>
      </TableCell>

      <TableCell>
        <Badge
          variant={statusBadge.variant}
          size="sm"
          className="rounded-full text-sm"
        >
          {statusBadge.label}
        </Badge>
      </TableCell>

      <TableCell>
        <Badge
          variant={is_public ? 'info-soft' : 'muted'}
          size="sm"
          className="rounded-full text-sm"
        >
          {is_public ? 'Público' : 'Privado'}
        </Badge>
      </TableCell>

      <TableCell>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canManage && status === 'draft' && (
            <PlayfulCardButton
              variant="outline"
              className="h-7 w-auto px-4 text-sm"
              onClick={handlePublish}
            >
              Publicar
            </PlayfulCardButton>
          )}

          {status === 'published' && (canManage || is_public) && (
            <PlayfulCardButton
              variant="outline"
              className="h-7 w-auto px-4 text-sm"
              onClick={handleActivate}
            >
              Ativar
            </PlayfulCardButton>
          )}

          {canManage && (
            <PlayfulCardButton
              className="-mt-[3px] h-7 w-auto px-4 text-sm"
              onClick={() => navigate(`/admin/desafio-ao-vivo/${id}/ativacoes`)}
            >
              Ativações{typeof challenge.activations_count === 'number' ? ` (${challenge.activations_count})` : ''}
            </PlayfulCardButton>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <PlayfulCardButton
                variant="outline"
                aria-label="Ações"
                className="h-7 w-7 flex-none gap-0 px-0"
              >
                <FiChevronDown size={16} />
              </PlayfulCardButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleDuplicate}>Duplicar para editar</DropdownMenuItem>

              {canManage && status === 'draft' && <DropdownMenuItem onClick={() => navigate(`/admin/desafio-ao-vivo/editar/nome/${id}`)}>Editar</DropdownMenuItem>}

              {isAuthor && status === 'draft' && <DropdownMenuItem onClick={() => setConfirmingDelete(true)}>Excluir</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {confirmingDelete && (
          <ConfirmDeleteDialog
            challengeTitle={title}
            onConfirm={handleDelete}
            onCancel={() => setConfirmingDelete(false)}
            loading={deleting}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

function ConfirmDeleteDialog({ challengeTitle, onConfirm, onCancel, loading }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/60 px-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-dropshadow-lg"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-foreground">Excluir desafio?</h3>
        <p className="mt-2 text-sm text-neutral">
          O desafio <strong>{challengeTitle}</strong> será removido permanentemente.
        </p>
        <p className="mt-2 text-xs text-neutral-light">
          Desafios duplicados a partir deste <strong>não</strong> serão afetados — eles têm cópias próprias.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function LiveChallengeList() {
  const { session } = useSession();
  const navigate = useNavigate();
  const currentUserId = session?.user?.id;
  const profileNames = session?.user?.profileNames || session?.user?.['profile-names'] || [];
  const isAdmin = profileNames.includes('Administrador');
  const isTeacher = profileNames.includes('Professor');
  const hasLiveChallengeReportPermission = usePermission('Visualizar Relatório de Desafio Ao Vivo', 'Relatórios');
  const canViewActivationHistory = isTeacher && hasLiveChallengeReportPermission;
  const playfulTeacher = usePlayfulTeacher();

  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const breadcrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [
      {
        route: '/admin/desafio-ao-vivo',
        name: 'Desafio ao Vivo',
        isActive: true
      }
    ]
  };

  const normalizeJsonApi = items =>
    items.map(item => ({
      id: item.id,
      ...Object.fromEntries(Object.entries(item.attributes).map(([k, v]) => [k.replace(/-/g, '_'), v]))
    }));

  const fetchChallenges = async () => {
    setLoading(true);
    try {
      const response = await liveChallengesApi.index();
      const items = response?.data ?? [];
      setChallenges(normalizeJsonApi(items));
    } catch {
      toast.error('Erro ao carregar desafios ao vivo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChallenges();
  }, []);

  const filteredChallenges = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return challenges.filter(c => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (normalizedSearch && !c.title?.toLowerCase().includes(normalizedSearch)) return false;
      return true;
    });
  }, [challenges, statusFilter, searchTerm]);

  // Aba ativa chanfrada (relevo 3D igual aos botões), mesmo padrão de course-tabs.jsx.
  const playfulTab = playfulTeacher
    ? 'data-[state=active]:bg-[var(--color-mission)] data-[state=active]:text-white data-[state=active]:font-extrabold data-[state=active]:shadow-[0_3px_0_var(--color-mission-dark)] data-[state=active]:-translate-y-px'
    : undefined;

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {playfulTeacher && (
        <div className="relative mb-6 sm:mt-16">
          <TiaBanner
            className="h-full"
            image={DESAFIO_AO_VIVO}
            imageAlt="Personagem do professor"
            imageHeight={190}
            imageOffset={250}
            bubbleCorner="tl"
            bubbleMaxWidth={520}
            text="Hora do desafio! 🎮 Monte disputas em tempo real e veja a turma competir ao vivo por cada resposta certa."
          />
        </div>
      )}

      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <Tabs
          value={statusFilter}
          onValueChange={setStatusFilter}
        >
          <TabsList className={playfulTeacher ? 'border border-neutral-lighter bg-white' : undefined}>
            {STATUS_FILTERS.map(f => (
              <TabsTrigger
                key={f.value}
                value={f.value}
                className={playfulTab}
              >
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative md:w-72">
          <FiSearch
            aria-hidden
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-light"
          />
          <Input
            startIcon
            type="search"
            placeholder="Buscar por título..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {canViewActivationHistory &&
            (playfulTeacher ? (
              <PlayfulCardButton
                className="h-9 w-auto px-6"
                onClick={() => navigate(`/relatorio/desafio-ao-vivo/${currentUserId}`)}
                variant="outline"
              >
                Meu histórico de ativações
              </PlayfulCardButton>
            ) : (
              <Button
                asChild
                variant="outline"
                size="sm"
              >
                <Link to={`/relatorio/desafio-ao-vivo/${currentUserId}`}>Meu histórico de ativações</Link>
              </Button>
            ))}

          {playfulTeacher ? (
            <PlayfulCardButton
              className="h-9 w-auto px-6"
              onClick={() => navigate('/admin/desafio-ao-vivo/criar/nome')}
            >
              Criar Desafio
            </PlayfulCardButton>
          ) : (
            <Button
              asChild
              variant="default"
              size="sm"
            >
              <Link to="/admin/desafio-ao-vivo/criar/nome">Criar Desafio</Link>
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : filteredChallenges.length > 0 ? (
        playfulTeacher ? (
          // min-w garante o piso da coluna "Desafio" (a única sem `width`,
          // logo a que table-layout:fixed trata como flexível): sem esse piso
          // na tabela, ela ou sumia com pouco espaço (a sobra negativa caía
          // toda nela) ou, quando demos `width` fixo a ela pra resolver isso,
          // sumiu a coluna flexível e o browser passou a jogar toda sobra de
          // espaço (tela larga) na última coluna com width (Ações, 420px).
          <Table className="min-w-[1100px]">
            <TableHeaderWrapper tableHead={CHALLENGE_TABLE_HEAD} />
            <TableBody>
              {filteredChallenges.map(challenge => (
                <ChallengeTableRow
                  key={challenge.id}
                  challenge={challenge}
                  onRefresh={fetchChallenges}
                  currentUserId={currentUserId}
                  isAdmin={isAdmin}
                />
              ))}
            </TableBody>
          </Table>
        ) : (
          <ul className="overflow-hidden rounded-xl bg-white shadow-dropshadow-sm [&>li:last-child]:border-b-0">
            {filteredChallenges.map(challenge => (
              <ChallengeRow
                key={challenge.id}
                challenge={challenge}
                onRefresh={fetchChallenges}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
              />
            ))}
          </ul>
        )
      ) : challenges.length === 0 ? (
        <EmptyState type="data" />
      ) : (
        // EmptyState não tem o type 'search' — caía no default 'Insira um texto.'
        // sem imagem. Reaproveitamos a ilustração de 'data' com texto explícito.
        <EmptyState
          type="data"
          text="Nenhum desafio encontrado para este filtro."
        />
      )}
    </DashboardContent>
  );
}
